import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { chatStoreInstance } from "@/components/VibeChat/hooks/useVibeChat";
import { handleVibeChatToolResultInvalidations } from "@/components/VibeChat/utils/invalidateSmartTableQueries";
import useLocalStorageState, { LOCAL_STORAGE_KEYS } from "@/hooks/useLocalStorageState";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";

/**
 * Part of the onboarding flow
 *
 * User selects a use case during onboarding and this hook is used to send the use case message to the AI.
 * Once the use case message is sent, the hook is used to invalidate the queries and redirect the user to the workspace.
 */
export const useOnboarding = () => {
  const chatStore = chatStoreInstance;
  const workspace = useActiveWorkspace();
  const queryClient = useQueryClient();
  const hasSentRef = useRef(false);

  const [onboardingLSUseCase, setOnboardingLSUseCase] = useLocalStorageState<string | null>(
    LOCAL_STORAGE_KEYS.ONBOARDING_USECASE,
    null,
  );

  useEffect(() => {
    if (hasSentRef.current || !onboardingLSUseCase || !workspace?.id || chatStore.isLoading) {
      return;
    }

    hasSentRef.current = true;

    const workspaceId =
      typeof workspace.id === "string" ? parseInt(workspace.id, 10) : workspace.id;

    const invalidateAll = () => {
      queryClient.invalidateQueries({ queryKey: ["folder-entities"] });
      queryClient.invalidateQueries({ queryKey: ["recent-vibe-chat-sessions"] });
    };

    // Clear localStorage immediately so a page reload doesn't re-send the message
    setOnboardingLSUseCase(null);

    chatStore.handleSendMessage(onboardingLSUseCase, workspaceId, {
      onStreamComplete: invalidateAll,
      onAgentProgress: () => queryClient.invalidateQueries({ queryKey: ["folder-entities"] }),
      onAgentComplete: invalidateAll,
      onToolResult: payload =>
        handleVibeChatToolResultInvalidations(queryClient, workspaceId, payload),
    });
  }, [
    onboardingLSUseCase,
    workspace?.id,
    chatStore.isLoading,
    chatStore,
    queryClient,
    setOnboardingLSUseCase,
  ]);
};
